import React from 'react';
import { Container, Row, Col, Card, Badge, Button } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faChartLine,
    faExclamationTriangle, faLightbulb, faRocket, faArrowRight,
    faDesktop, faServer, faDatabase, faChartBar, faRandom,
    faSquareParking, faClock, faShieldAlt
} from '@fortawesome/free-solid-svg-icons';
import { Link } from 'react-router-dom';
import SEO from '../SEO/SEO';

import pyramid from "../../assets/images/use-cases/Legacy-System-Risk.png";
import vis from "../../assets/images/use-cases/Legacy-System-UX.png";

const LegacySystemRiskMitigation = () => {
    return (
        <>
            <SEO
                title="Legacy System Risk Mitigation: AI Digital Twin for Platform Migration | Nebulyx AI"
                description="De-risk your legacy system migration using AI Digital Twins. Model black box behaviour, map hidden business rules, and simulate the new business process before committing to change."
                keywords="AI digital twin, legacy system migration, black box system, platform transformation, risk mitigation, parking booking system"
                canonicalUrl="https://nebulyx.ai/use-cases/legacy-system-risk-mitigation"
                ogType="article"
            />

            <article className="use-case-page">
                {/* Hero */}
                <header className="use-case-hero">
                    <Container>
                        <Row>
                            <Col className={"w-100"}>
                                <div className={"w-100 text-center"}>
                                    <div >
                                        <Badge className="sector-badge m-1 mb-4 h-100">
                                            <FontAwesomeIcon icon={faSquareParking}/>
                                            &nbsp;
                                            Transport & Property
                                        </Badge>
                                        <h1 className="use-case-title">Question:&nbsp;How do we safely migrate away from our black box booking system?</h1>
                                    </div>
                                </div>
                                <div>
                                    <p className="use-case-subtitle w-100 text-center">
                                        Model your legacy system's hidden behaviour, map undocumented business rules, and simulate the new process to de-risk your platform transformation.
                                    </p>
                                </div>
                            </Col>
                        </Row>

                        <Row className="hero-stats-inline ">
                            <Col className="stat-item-inline">
                                <FontAwesomeIcon icon={faClock} className="stat-icon"/>
                                <span className="stat-value">85%</span>
                                <span className="stat-label">Risk Reduction</span>
                            </Col>
                            <Col  className="stat-item-inline">
                                <FontAwesomeIcon icon={faShieldAlt} className="stat-icon"/>
                                <span className="stat-value">30</span>
                                <span className="stat-label">Processes Mapped</span>
                            </Col>
                            <Col  className="stat-item-inline">
                                <FontAwesomeIcon icon={faChartLine} className="stat-icon"/>
                                <span className="stat-value">Fast</span>
                                <span className="stat-label">Migration to new system</span>
                            </Col>
                        </Row>

                    </Container>
                </header>

                {/* Executive Summary */}
                <section className="exec-summary-section">
                    <Container>
                        <h2 className="section-title">Executive Summary</h2>
                        <Row className="g-4">
                            <Col md={4}>
                                <Card className="summary-card h-100">
                                    <Card.Body>
                                        <h3 className={"text-start"}><span className={"card-icon-wrapper problem me-1"}><FontAwesomeIcon
                                            sty icon={faExclamationTriangle}/></span>The Problem</h3>
                                        <ul className="summary-list">
                                            <li>Legacy booking system is a "black box" with no documentation</li>
                                            <li>Critical business rules embedded in decades-old code</li>
                                            <li>Real-time availability and pricing logic is opaque</li>
                                            <li>Migration risk could cause booking failures and revenue loss</li>
                                        </ul>
                                    </Card.Body>
                                </Card>
                            </Col>
                            <Col md={4}>
                                <Card className="summary-card h-100">
                                    <Card.Body>
                                        <h3 className={"text-start"}><span
                                            className={"card-icon-wrapper solution me-1"}><FontAwesomeIcon
                                            sty icon={faLightbulb}/></span>The Solution</h3>
                                        <ul className="summary-list">
                                            <li>Build a Data Object Graph of booking flows and outcomes</li>
                                            <li>Reverse-engineer hidden business processes from actual data</li>
                                            <li>Create a Digital Twin that creates the new Digital way of working</li>
                                            <li>Test processes with the business before any major build</li>
                                        </ul>
                                    </Card.Body>
                                </Card>
                            </Col>
                            <Col md={4}>
                                <Card className="summary-card h-100">
                                    <Card.Body>
                                        <h3 className={"text-start"}><span
                                            className={"card-icon-wrapper benefits me-1"}><FontAwesomeIcon
                                            sty icon={faRocket}/></span>The Benefits</h3>
                                        <ul className="summary-list">
                                            <li>Documented map of all hidden business processes</li>
                                            <li>Validated new platform before cutover</li>
                                            <li>Parallel running with real-time comparison</li>
                                            <li>Confident migration with rollback procedures</li>
                                        </ul>
                                    </Card.Body>
                                </Card>
                            </Col>
                        </Row>
                    </Container>
                </section>

                {/* Process Steps */}
                <section className="content-section process-section">
                    <Container>
                        <h2 className="section-title">The AI Digital Twin Process</h2>
                        <p className="text-center text-muted mb-4">
                            Ingests legacy system transactions, booking outcomes, and pricing data to build a Data Object Graph that mirrors the process and enables safe migration testing.
                        </p>
                        <div className="process-steps-container">
                            <div className="process-step-item">
                                <div className="step-number">1</div>
                                <div className="step-content">
                                    <strong>Capture Behaviour</strong>
                                    <span>Transaction data, booking outcomes, pricing decisions, error patterns</span>
                                </div>
                            </div>
                            <div className="step-arrow">
                                <FontAwesomeIcon icon={faArrowRight}/>
                            </div>
                            <div className="process-step-item">
                                <div className="step-number">2</div>
                                <div className="step-content">
                                    <strong>Build Graphs</strong>
                                    <span>Data Object Graph implement all process flows, pricing logic, and booking constraints</span>
                                </div>
                            </div>
                            <div className="step-arrow">
                                <FontAwesomeIcon icon={faArrowRight}/>
                            </div>
                            <div className="process-step-item">
                                <div className="step-number">3</div>
                                <div className="step-content">
                                    <strong>Simulate Actual Process</strong>
                                    <span>What-if engine tests new platform against all outcomes</span>
                                </div>
                            </div>
                            <div className="step-arrow">
                                <FontAwesomeIcon icon={faArrowRight}/>
                            </div>
                            <div className="process-step-item">
                                <div className="step-number">4</div>
                                <div className="step-content">
                                    <strong>Fast Build </strong>
                                    <span>DOG provides all requirements for dev build and confident go-live</span>
                                </div>
                            </div>
                        </div>
                    </Container>
                </section>

                {/* AI Digital Twin - Two Sections */}
                <section className="content-section twin-section">
                    <Container>
                        <h2 className="section-title">The AI Digital Twin</h2>
                        <Row className="g-4">
                            {/* Back-end Architecture */}
                            <Col lg={6}>
                                <Card className="twin-view-card h-100">
                                    <Card.Body>
                                        <div className="twin-view-header">
                                            <div className="twin-view-icon backend">
                                                <FontAwesomeIcon icon={faServer}/>
                                            </div>
                                            <h3>Digital Twin Process Flow</h3>
                                        </div>
                                        <p className="twin-view-description">
                                            Ingests data from legacy booking transactions, availability responses, and pricing outcomes to model
                                            the black box system's behaviour end-to-end. Test flows to capture undocumented business rules
                                            and edge cases that could cause migration failures.</p>
                                        <div className="image-parent">
                                            <img className={"image-child-span w-100 h-100"} src={pyramid} alt=""/>
                                        </div>
                                    </Card.Body>
                                </Card>
                            </Col>

                            {/* Front-end UI */}
                            <Col lg={6}>
                                <Card className="twin-view-card h-100">
                                    <Card.Body>
                                        <div className="twin-view-header">
                                            <div className="twin-view-icon frontend">
                                                <FontAwesomeIcon icon={faDesktop}/>
                                            </div>
                                            <h3>Real User Experience</h3>
                                        </div>
                                        <p className="twin-view-description">
                                            Delivers fully functional versions of the new platform screens for business teams to use directly. Users can run real booking scenarios, test edge cases, and verify outcomes match requirements. Builds confidence through hands-on experience before go-live.</p>
                                        <div className="image-parent">
                                            <img className={"image-child-span w-100 h-100"} src={vis} alt=""/>
                                        </div>
                                    </Card.Body>
                                </Card>
                            </Col>
                        </Row>


                        {/* Twin Components */}
                        <Row className="g-4 mt-4">
                            <Col md={4}>
                                <Card className="twin-card h-100">
                                    <Card.Body>
                                        <div className="twin-card-header">
                                            <div className="twin-icon">
                                                <FontAwesomeIcon icon={faDatabase}/>
                                            </div>
                                            <h4>Data Analysed</h4>
                                        </div>
                                        <ul className="twin-list">
                                            <li>Booking data</li>
                                            <li>Car Part data</li>
                                            <li>Pricing</li>
                                            <li>Customer</li>
                                            <li>Historical Demand</li>
                                            <li>Busienss Logic(Inc:Cancellation & Refund)</li>
                                            <li>Partner Integration Flows</li>
                                        </ul>
                                    </Card.Body>
                                </Card>
                            </Col>
                            <Col md={4}>
                                <Card className="twin-card h-100">
                                    <Card.Body>
                                        <div className="twin-card-header">
                                            <div className="twin-icon">
                                                <FontAwesomeIcon icon={faChartBar}/>
                                            </div>
                                            <h4>AI / Data Products</h4>
                                        </div>
                                        <ul className="twin-list">
                                            <li>Workflow Engine</li>
                                            <li>Transactional Microservices</li>
                                            <li>Business rule engine</li>
                                            <li>Pricing models</li>
                                            <li>Payment Gateways</li>
                                            <li>Customer profile engine</li>
                                            <li>Success Metrics</li>
                                        </ul>
                                    </Card.Body>
                                </Card>
                            </Col>
                            <Col md={4}>
                                <Card className="twin-card h-100">
                                    <Card.Body>
                                        <div className="twin-card-header">
                                            <div className="twin-icon">
                                                <FontAwesomeIcon icon={faRandom}/>
                                            </div>
                                            <h4>Scenarios Tested</h4>
                                        </div>
                                        <ul className="twin-list">
                                            <li>Booking process simulation</li>
                                            <li>Partner API timeout handling</li>
                                            <li>Pricing</li>
                                            <li>Concurrent amendment conflicts</li>
                                            <li>Rollback procedure validation</li>
                                        </ul>
                                    </Card.Body>
                                </Card>
                            </Col>
                        </Row>

                        {/* Discovery */}
                        <div className="discovery-highlight mt-5">
                            <Row className="align-items-center">
                                <Col lg={8}>
                                    <h3>The Discovery</h3>
                                    <p>
                                        By modelling the legacy system as a <strong>Data Object Graph</strong>, we can reveal
                                        undocumented business processes and rules that have accumulated over years of incremental changes.
                                        <strong> We can reverse engineer hidden processes and business rules</strong>
                                        {" "}to not have to start from scratch or miss important nuance in the operating model. Parallel running
                                        validates every booking outcome before to de-risk any major build of the new system.
                                    </p>
                                </Col>
                                <Col lg={4}>
                                    <div className="discovery-stat">
                                        <span className="discovery-label">Optimal Strategy Found</span>
                                        <span className="discovery-value">Parallel Validation</span>
                                        <span className="discovery-note">30 Processses and 127 rules mapped</span>
                                    </div>
                                </Col>
                            </Row>
                        </div>
                    </Container>
                </section>

                {/* CTA */}
                <section className="cta-section">
                    <Container>
                        <div className="cta-content">
                            <h2>Facing a Legacy System Migration?</h2>
                            <p>
                                If your critical booking system is a black box and migration risk is holding back transformation,
                                let's build your Digital Twin together.
                            </p>
                            <div className="cta-buttons">
                                <Button
                                    variant="light"
                                    size="lg"
                                    href="https://cal.eu/jon-cooke/discovery-call"
                                    className="me-3 mb-2"
                                >
                                    Book a Discovery Call
                                    <FontAwesomeIcon icon={faArrowRight} className="ms-2"/>
                                </Button>
                                <Link to="/use-cases">
                                    <Button variant="outline-light" size="lg" className="mb-2">
                                        View More Use Cases
                                    </Button>
                                </Link>
                            </div>
                        </div>
                    </Container>
                </section>
            </article>
        </>
    );
};

export default LegacySystemRiskMitigation;